import { jwtDecode } from "jwt-decode";
import { createContext, useState, useEffect, useContext } from "react";
import { toast } from "react-hot-toast";
import { io } from "socket.io-client";

const NotificationContext = createContext();

export const useNotifications = () => useContext(NotificationContext);

let socket; // Shared socket instance

export const NotificationProvider = ({ children }) => {
  const [updates, setUpdates] = useState([]);
  const [lastSync, setLastSync] = useState(null);
  const [loading, setLoading] = useState(true);
  const [postObjectID, setPostObjectID] = useState(0);

  const fetchSocket = () => {
    let isMounted = true;

    const token = localStorage.getItem("accessToken");
    if (token && isMounted) {
      try {
        const decoded = jwtDecode(token);
        const instanceID = decoded?.instanceID;

        if (!instanceID && instanceID !== null) {
          console.warn("❗ instanceID not found in token.");
          return;
        }

        setPostObjectID(instanceID);

        fetchUpdates(instanceID);

        // ✅ Initialize socket connection
        socket = io(process.env.REACT_APP_NODEJS_SERVER_URL, {
          auth: { token },
        });

        socket.on("connect", () => {
          console.log("✅ Connected to socket:", socket.id);
          socket.emit("joinInstance", instanceID);
        });

        socket.on("newFeed", (data) => {
          console.log("📩 New feed via socket:", data);
          if(!isMounted) return;
          const instanceId = data.instanceId;
          const from = data.user?.first_name || "Unknown";
          const message = data?.message || "New Feed Received";

          const instanceSlug = localStorage.getItem("instanceSlug") || "default-slug";

          toast(
            <div style={{ cursor: "pointer", textDecoration: "underline" }} onClick={() => window.location.href = `${process.env.REACT_APP_NODEJS_CLI_URL}/instance/${instanceSlug}`}
            >
              <h6>New Post from <b>{from}</b></h6>
              {message.slice(0, 50)}...
            </div>,
            {
              position: "top-right",
              duration: 10000,
              pauseOnHover: true,
            }
          );

          if (data.createdAt) setLastSync(data.createdAt);
          fetchUpdates(); // Refresh feed list
        });

        socket.on("disconnect", () => {
          console.warn("🔌 Socket disconnected");
        });
      } catch (error) {
        console.error("JWT decode error:", error);
      }
    }

    // ✅ Cleanup on unmount
    return () => {
      isMounted = false;
      if (socket && socket.connected) {
        socket.disconnect();
        console.log("🔌 Socket cleaned up on unmount.");
      }
    };
  }

  useEffect(() => {
    fetchSocket();
  }, []);

  const fetchUpdates = async () => {

    if (postObjectID === null || typeof postObjectID === "undefined") {
      console.warn("❌ Skipping fetchUpdates due to invalid postObjectID:", postObjectID);
      return;
    }

    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(
        `${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/?instanceId=${postObjectID}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      const data = await response.json();
      setUpdates(data);
      if (data.length > 0) setLastSync(data[0]?.createdAt);
    } catch (err) {
      console.error("Error fetching updates:", err);
    }
  };

  const fetchNotification = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const url = new URL(`${process.env.REACT_APP_NODEJS_SERVER_URL}/news-feed/`);
      url.searchParams.append("instanceId", postObjectID);
      url.searchParams.append("limit", 1);

      if (lastSync && lastSync !== "null") {
        url.searchParams.append("lastSync", lastSync);
      } else {
        console.log("⏳ Skipping fallback polling due to invalid lastSync");
        return;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (data.length > 0) {
        console.log("🔁 New feed via fallback polling:", data);

        const from = data[0]?.user?.first_name || "Unknown";
        const message = data[0]?.message || "No message content";

        toast(
          <div>
            <h6>New Post from <b>{from}</b></h6>
            {message.slice(0, 50)}...
          </div>,
          { position: "top-right", duration: 10000 }
        );

        setLastSync(data[0]?.createdAt);
        fetchUpdates();
      }
    } catch (error) {
      console.error("Polling fallback error:", error);
    }
  };

  useEffect(() => {
    if (!postObjectID || postObjectID === "undefined") return;

    fetchUpdates();

    const interval = setInterval(() => {
      fetchNotification();
    }, 60000); // every 10 seconds

    return () => clearInterval(interval);
  }, [lastSync, postObjectID]);

  return (
    <NotificationContext.Provider value={{ updates, lastSync, loading, fetchSocket }}>
      {children}
    </NotificationContext.Provider>
  );
};


export const getSocket = ()=> socket;
